import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { getLocale } from '@hub/libs/constants';
import { EXTERNAL_LINKS } from '@hub/libs/redirect.helper';
import { useDebouncedCallback } from 'use-debounce';
import { productSearchAutocomplete } from '@hub/libs/catalog-service';
import { Icon, IconID } from '@hub/common/Icon/Icon';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/nextjs';

export const MobileSearchBox = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const [autocompleteVisible, setAutocompleteVisible] = useState<boolean>(false);
  const { t } = useTranslation('layout/header');
  const router = useRouter();
  const locale = getLocale(router);

  // go to search result page
  const executeSearch = (term: string) => {
    window.location.assign(EXTERNAL_LINKS.SEARCH(locale) + term);
  };

  const handleBlur = (event: React.FocusEvent) => {
    setAutocompleteVisible(true);

    // If the textinput lost focus because a autocomplete item was clicked the
    // blur will triggered before the click event. Because of this we need to
    // check which item received the focus and act accordingly.
    if (
      event.relatedTarget instanceof HTMLElement &&
      event.relatedTarget.id.startsWith('searchbox-autocomplete-link-')
    ) {
      executeSearch(searchTerm);
    }
  };

  const debouncedSearch = useDebouncedCallback(async (input: string) => {
    const inputValue = input.trim();

    if (inputValue.length >= 2) {
      try {
        await productSearchAutocomplete(inputValue).then(results => {
          setSearchResults(results);
          setAutocompleteVisible(true);
        });
      } catch (error) {
        Sentry.captureException(new Error(`Could not autocomplete - ${error as string}`));
      }
    } else {
      setSearchResults([]);
      setAutocompleteVisible(false);
    }
  }, 500);

  return (
    <>
      <input
        data-testid="mobilesearchbox-input"
        type="text"
        className="outline-none flex-grow py-5 px-8 text-base text-gray-300 placeholder-gray-300"
        value={searchTerm}
        onChange={async e => {
          setSearchTerm(e.target.value);
          try {
            await debouncedSearch(e.target.value);
          } catch (error) {
            Sentry.captureException(
              new Error(`Could not debouncedSearch with value: ${e.target.value} - ${error as string}`)
            );
          }
        }}
        onBlur={handleBlur}
        placeholder={t('HEADER_SEARCH.INPUT_PLACEHOLDER')}
      />
      {autocompleteVisible && (
        <div
          data-testid="mobilesearchbox-clear-search"
          className="cursor-pointer rounded-3xl text-gray-400 absolute top-4 right-32 p-3 z-10 bg-gray-100"
          onClick={() => {
            setSearchTerm('');
            setAutocompleteVisible(false);
          }}
        >
          <Icon id={IconID.CLOSE_CROSS} size={5} primary={false} />
        </div>
      )}
      {autocompleteVisible && searchResults.length > 0 && (
        <ul
          data-testid="searchbox-autocomplete"
          className="text-2xl absolute top-20 border-t border-gray-100 w-full shadow-lg bg-white z-50 divide-y divide-gray-100 overflow-y-auto"
          style={{ height: 'calc(100vh - 5rem)' }}
        >
          {searchResults.map((term, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={index}>
              <a
                id={`searchbox-autocomplete-link-${index}`}
                data-testid={`searchbox-autocomplete-link-${index}`}
                // needed to make this an event that can receive focus and so appears in the focus event of textinput blur
                tabIndex={0}
                className="px-8 py-5 w-full inline-block cursor-pointer"
              >
                {term}
              </a>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};
